import { DeepReadonlyObject } from 'rxdb';

import { ItineraryView, TripTypeEnum } from '@mopla/data-models';
import {
	formatPriceFromCents,
	getItineraryTimingV2,
	getLegDuration,
} from '@mopla/utils';

import { IIntermediateStop, IRouteStop, TTransportMode } from './types';

export enum ERouteStopVariant {
	START = 'start',
	END = 'end',
	INTERMEDIATE = 'intermediate',
}

export const getRouteStopVariant = (
	idx: number,
	total: number
): ERouteStopVariant => {
	if (idx === 0) return ERouteStopVariant.START;
	if (idx === total - 1) return ERouteStopVariant.END;
	return ERouteStopVariant.INTERMEDIATE;
};

/**
 * Maps an ItineraryView from the API to the IRouteStop[] format used by BookingRoute components.
 * Each leg becomes a stop with a legAfter descriptor; the final leg's "to" becomes the last stop.
 */
export function mapItineraryToRouteStops(
	itinerary: ItineraryView | DeepReadonlyObject<ItineraryView>,
	legPrices?: Record<string, number>,
	defaultLbodtflexLabel?: string
): IRouteStop[] {
	const { legs } = itinerary;
	if (!legs.length) return [];

	const firstLeg = legs[0];
	const firstLegTiming = getItineraryTimingV2([firstLeg]);

	const stops: IRouteStop[] = [
		{
			time: firstLegTiming.startDateObj.toISOString(),
			effectiveTime: firstLegTiming.isStartTimeChanged
				? firstLegTiming.effectiveStartDateObj.toISOString()
				: undefined,
			address: firstLeg.from.address
				? firstLeg.from.address
				: firstLeg.from.name,
			lat: parseFloat(firstLeg.from.lat),
			lng: parseFloat(firstLeg.from.lng),
		},
	];

	legs.forEach((leg) => {
		const currentStop = stops[stops.length - 1];

		if (
			leg.mode === TripTypeEnum.WALK ||
			leg.mode === TripTypeEnum.TRANSFER_WALK
		) {
			currentStop.legAfter = {
				mode: leg.mode,
				durationMinutes: getLegDuration(leg) ?? 0,
				distanceMeters: Math.round(leg.distance ?? 0),
			};
		} else {
			const intermediateStops: IIntermediateStop[] | undefined =
				leg.intermediateStops?.map((s) => ({
					time: s.arrival ?? s.departure ?? '',
					name: s.name ?? '',
				}));

			const price =
				leg.mode === TripTypeEnum.LBODTFLEX && legPrices?.[leg.legId] != null
					? formatPriceFromCents(legPrices[leg.legId])
					: undefined;

			currentStop.legAfter = {
				mode: leg.mode as TTransportMode,
				lineNumber: leg.route ?? undefined,
				lineName:
					leg.mode === TripTypeEnum.LBODTFLEX
						? leg.lbodtflexLabel ?? defaultLbodtflexLabel ?? ''
						: leg.headsign ?? '',
				duration: leg.duration ?? 0,
				price,
				intermediateStops: intermediateStops?.length
					? intermediateStops
					: undefined,
			};
		}

		const legTiming = getItineraryTimingV2([leg]);

		stops.push({
			time: legTiming.endDateObj.toISOString(),
			effectiveTime: legTiming.isEndTimeChanged
				? legTiming.effectiveEndDateObj.toISOString()
				: undefined,
			address: leg.to.address ? leg.to.address : leg.to.name,
			lat: parseFloat(leg.to.lat),
			lng: parseFloat(leg.to.lng),
		});
	});

	return stops;
}
